import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import { ReactReduxContext } from 'react-redux';
import isEqual from 'lodash/isEqual';
import mustache from 'mustache';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@chargebee/sting-react';
import { preauthCompanySlice } from '@bbkAdminRedux/rtkq/preauth-company.slice';
import { FunnelContext } from '@bbkFunnel/components/FunnelContext';
import {
  EventSource,
  type EventObject,
  type ExecuteEventsHandler,
} from '@bbkFunnel/events/events';
import { PricingPageShimmerLoader } from '@bbkPricingPage/components/loader/pricing-page-shimmer-loader';
import {
  CbEventKeys,
  EventKeys,
  type CbSuccessEvent,
  type EventOptions,
  type SetBillingAddressEvent,
  type SetBusinessEntityEvent,
  type SetCurrencyEvent,
  type SetPriceVariantEvent,
  type SetQuantityEvent,
  type SetShippingAddressEvent,
  type SetShowPriceEvent,
  type SetSubscriptionAttributesEvent,
  type SetVisitorEvent,
  type ShowPrice,
} from '@bbkPricingPage/events/defn';
import useMessage from '@bbkPricingPage/events/listener';
import { withCamelCaseAliases } from '@bbkPricingPage/helpers/mustache-data';
import { PricingTableHelper } from '@bbkPricingPage/helpers/pricing-table-helper';
import { samplePricingTableV2 } from '@bbkPricingPage/pricing-table-mocks';
import {
  ActionButtonType,
  GroupedBy,
  SubscriberCTAAction,
  type Address,
  type CreateCheckoutRequest,
  type CreateCheckoutRequestAddon,
  type CreatePreviewCheckoutRequest,
  type Currency,
  type Discount,
  type Frequency,
  type HostedPage,
  type Item,
  type PaywallConfig,
  type Price,
  type PriceQuantities,
  type PricingTable,
  type Subscription,
  type SubscriptionAttributes,
  type Visitor,
} from '@bbkPricingPage/types/pricing-table-types';

const VISITOR_ID_KEY = 'growth.visitorId';

export const getOrCreateVisitorId = (): string => {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);

  if (!visitorId) {
    visitorId = uuidv4();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }

  return visitorId;
};

const loadChargebeeScript = (
  isProd: boolean,
  onLoad: () => void
): HTMLScriptElement | null => {
  const jsHost = isProd ? 'js.chargebee.com' : 'js.devcb.in';
  const chargebeeJsUrl = `https://${jsHost}/v2/chargebee.js`;

  const existingScript = document.querySelector(
    `script[src="${chargebeeJsUrl}"]`
  );
  if (existingScript) {
    if ((window as any).Chargebee) {
      onLoad();
    }
    return null;
  }

  const script = document.createElement('script');
  script.src = chargebeeJsUrl;
  script.async = true;
  script.onload = () => {
    onLoad();
  };
  document.body.appendChild(script);
  return script;
};

const initializeChargebee = (
  siteDomain: string | undefined,
  businessEntityId: string | undefined,
  checkoutCustomDomain: string | undefined
) => {
  // @ts-ignore
  if (siteDomain && window.Chargebee) {
    // @ts-ignore
    if (!window.Chargebee.inited) {
      // @ts-ignore
      window.Chargebee.init({
        site: siteDomain,
        domain: checkoutCustomDomain,
        businessEntityId,
        enableTopLevelRedirectOnSuccess: true,
        enableGTMTracking: true,
      });

      // @ts-ignore
      window.Chargebee.getInstance().getSiteInfo();
      // TODO: see if this is needed or not. Calling it for now to set the style.
    }

    // @ts-ignore
    return window.Chargebee.getInstance();
  }
};

export const PricingTableContext = createContext<{
  pricingTable: PricingTable | undefined;
  subscription: Subscription | undefined;
  currency: Currency;
  frequency: Frequency | null | undefined;
  priceVariantId: string | undefined;
  setCurrency: React.Dispatch<React.SetStateAction<Currency>>;
  setFrequency: (frequency: Frequency | null | undefined) => void;
  setPriceVariantId: (priceVariantId: string | undefined) => void;
  quantity: number | undefined;
  setQuantity: (quantity: number | undefined) => void;
  priceQuantities: PriceQuantities;
  setPriceQuantity: (priceId: string, quantity: number) => void;
  discounts?: Discount[];
  country?: string;
  defaultCurrency?: string;
  isPreview: boolean;
  isFullPreview: boolean;
  showCurrencyDropdown: boolean;
  urlCurrencyCode?: string;
  /** Atomic Pricing site id from /sites/{siteId}/pricing/{pageId} URLs */
  legacySiteId?: string;
  /** Atomic Pricing page id from /sites/{siteId}/pricing/{pageId} URLs */
  legacyPricingPageId?: string;
  isCheckoutLoading: boolean;
  handlePrimaryCtaClick: (props: {
    item: Item;
    price: Price;
    quantity: number;
    subscription?: Subscription;
    isSubscribedPrice?: boolean;
    paywallConfig?: PaywallConfig;
    addonsDataForCheckout?: any[];
    recommendedAddonsMap?: Record<string, number>;
    mandatoryAddonsMap?: Record<string, number>;
  }) => void;
  executeEvents: ExecuteEventsHandler;
  visitor: Visitor;
  showPrice: ShowPrice;
}>(null!);

export const PricingTableContextProvider: FC<
  PropsWithChildren<{
    pricingTable?: PricingTable;
    subscription?: Subscription;
    discounts?: Discount[];
    country?: string;
    defaultCurrency?: string;
    host?: string;
    urlCurrency?: string;
    urlPeriodUnit?: string;
    urlPeriod?: number;
    priceVariantId?: string;
    showCurrencyDropdown?: boolean;
    executeEvents: ExecuteEventsHandler;
  }>
> = ({
  children,
  pricingTable: pricingTableFromProps,
  subscription: subscriptionFromProps,
  discounts,
  country,
  defaultCurrency,
  host: hostFromProps,
  urlCurrency,
  urlPeriodUnit,
  urlPeriod,
  priceVariantId: priceVariantIdFromProps,
  showCurrencyDropdown: showCurrencyDropdownProp = true,
  executeEvents,
}) => {
  const initialPricingTable =
    pricingTableFromProps ?? (samplePricingTableV2 as unknown as PricingTable);
  const [pricingTable, setPricingTable] =
    useState<PricingTable>(initialPricingTable);
  const [currency, setCurrency] = useState<Currency>(() => {
    const allAttached =
      PricingTableHelper.getCurrenciesFromPricingTable(initialPricingTable);
    const attached = subscriptionFromProps?.currency
      ? allAttached.filter(
          (c) => c.currency_code === subscriptionFromProps.currency
        )
      : allAttached;

    if (urlCurrency) {
      const found = attached.find((c) => c.currency_code === urlCurrency);
      if (found) return found;
    }
    return (attached[0] ?? allAttached[0]) as Currency;
  });
  const [frequency, setFrequency] = useState<Frequency | null | undefined>(
    () => {
      if (initialPricingTable.grouped_by === GroupedBy.item_frequency) {
        return undefined;
      }

      const attached =
        PricingTableHelper.getFrequenciesFromPricingTable(initialPricingTable);

      if (urlPeriodUnit) {
        const period = urlPeriod ?? 1;
        const found = attached.find(
          (f) => f.period === period && f.period_unit === urlPeriodUnit
        );
        if (found) return found;
      }

      if (subscriptionFromProps?.period && subscriptionFromProps?.period_unit) {
        const subFreq = attached.find(
          (f) =>
            f.period === subscriptionFromProps.period &&
            f.period_unit === subscriptionFromProps.period_unit
        );
        if (subFreq) return subFreq;
      }

      return attached[0];
    }
  );

  const [priceVariantId, setPriceVariantId] = useState<string | undefined>(
    priceVariantIdFromProps
  );
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const {
    pricingPageProps,
    isPreview = false,
    isFullPreview = false,
  } = useContext(FunnelContext);

  // Check if Redux is available (admin pages only; funnel pages don't have Provider)
  const reduxContext = useContext(ReactReduxContext);
  const clientConfig = reduxContext?.store
    ? preauthCompanySlice.endpoints.getClientConfig.select()(
        reduxContext.store.getState()
      )?.data
    : undefined;

  const isProd =
    clientConfig?.is_prod ?? window.bbkClientConfig?.is_prod ?? true;
  const legacySiteId = pricingPageProps?.legacySiteId;
  const legacyPricingPageId = pricingPageProps?.legacyPricingPageId;

  // Undefined so PricingCard can fall through to quantity_meta.default_value.
  // SET_QUANTITY event overrides this as a global value.
  const [quantity, setQuantity] = useState<number | undefined>(undefined);

  const [priceQuantities, setPriceQuantities] = useState<PriceQuantities>({});

  const setPriceQuantity = useCallback((priceId: string, quantity: number) => {
    setPriceQuantities((prev) => {
      if (prev[priceId] === quantity) {
        return prev;
      }
      return { ...prev, [priceId]: quantity };
    });
  }, []);

  const [subscription, setSubscription] = useState<Subscription | undefined>(
    subscriptionFromProps
  );
  // Add state for visitor, addresses, etc.
  const [visitor, setVisitor] = useState<Visitor>({});
  const [subscriptionAttributes, setSubscriptionAttributes] =
    useState<SubscriptionAttributes>({});
  const [billingAddress, setBillingAddress] = useState<Address>({});
  const [shippingAddress, setShippingAddress] = useState<Address>({});
  const [businessEntityId, setBusinessEntityId] = useState<
    string | undefined
  >();

  const [countryState, setCountryState] = useState<string | undefined>(country);
  const [showPrice, setShowPrice] = useState<ShowPrice>({ configs: [] });
  const { toast } = useToast();

  // Helper function to handle fetch errors
  const handleFetchError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    const error: any = new Error(
      (errorData as any).message || 'Checkout request failed'
    );
    error.status = response.status;
    error.data = errorData;
    throw error;
  };

  const buildQueryString = (params: Record<string, string | undefined>) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });
    const qs = searchParams.toString();
    return qs ? `?${qs}` : '';
  };

  // Live checkout function
  const createCheckout = async (
    appKey: string,
    pricingPageId: string,
    createCheckoutData: CreateCheckoutRequest
  ): Promise<HostedPage> => {
    const queryString = buildQueryString({ variant_id: priceVariantId });
    const url = `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/checkout${queryString}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(createCheckoutData),
    });

    if (!response.ok) {
      await handleFetchError(response);
    }

    return (await response.json()) as HostedPage;
  };

  // Preview checkout function
  const createPreviewCheckout = async (
    appKey: string,
    pricingPageId: string,
    createPreviewCheckoutData: CreatePreviewCheckoutRequest
  ): Promise<HostedPage> => {
    const queryString = buildQueryString({
      publish_state: pricingPageProps?.publishState,
      variant_id: priceVariantId,
    });
    const url = `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/preview/checkout${queryString}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (pricingPageProps?.bbkAuthenticityToken) {
      headers['BBK-Authenticity-Token'] = pricingPageProps.bbkAuthenticityToken;
    }

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(createPreviewCheckoutData),
    });

    if (!response.ok) {
      await handleFetchError(response);
    }

    return (await response.json()) as HostedPage;
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'PRICING_PAGE_UPDATE') {
        const { country: newCountry, subscription: newSubscription } =
          event.data.data;

        if (newCountry !== undefined) {
          setCountryState(newCountry);
        }

        if (newSubscription !== undefined) {
          setSubscription(newSubscription);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Precedence order: priceVariantIdFromProps (price variant chosen in preview dropdown/embedded in URL) >>> price variant ID resolved from active subscription plan >>> default variant
  // UI V1 -> all prices fetched, simply filter and get variant ID
  // UI V2 -> price data passed to UI is already pre-filtered (variant + default for subscription flow only), get variant ID from plan using subscription price ID
  useEffect(() => {
    if (priceVariantIdFromProps != null) {
      setPriceVariantId(priceVariantIdFromProps);
    } else {
      setPriceVariantId(
        PricingTableHelper.getApplicablePriceVariant(
          pricingTable?.items ?? [],
          subscriptionFromProps
        )
      );
    }
  }, [priceVariantIdFromProps, pricingTable?.items, subscriptionFromProps]);

  useEffect(() => {
    setSubscription(subscriptionFromProps);
  }, [subscriptionFromProps]);

  useEffect(() => {
    if (!pricingTableFromProps) return;
    setPricingTable((prev) => {
      if (prev === pricingTableFromProps) return prev;
      if (isEqual(prev, pricingTableFromProps)) return prev;
      return JSON.parse(JSON.stringify(pricingTableFromProps)) as PricingTable;
    });
  }, [pricingTableFromProps]);

  useEffect(() => {
    if (pricingTable?.grouped_by === GroupedBy.item_frequency) {
      setFrequency(undefined);
      return;
    }

    const attachedFrequencies =
      PricingTableHelper.getFrequenciesFromPricingTable(pricingTable);

    if (attachedFrequencies.length === 0) {
      setFrequency(undefined);
      return;
    }

    const subscriptionFrequency =
      subscription?.period && subscription?.period_unit
        ? attachedFrequencies.find(
            (f) =>
              f.period === subscription.period &&
              f.period_unit === subscription.period_unit
          )
        : undefined;

    if (subscriptionFrequency) {
      setFrequency(subscriptionFrequency);
      return;
    }

    setFrequency((current) => {
      if (!current) return attachedFrequencies[0];
      const stillAttached = attachedFrequencies.find(
        (f) =>
          f.period === current.period && f.period_unit === current.period_unit
      );
      return stillAttached ?? attachedFrequencies[0];
    });
  }, [pricingTable, subscription]);

  useEffect(() => {
    const allAttached =
      PricingTableHelper.getCurrenciesFromPricingTable(pricingTable);
    const attachedCurrencies = subscription?.currency
      ? allAttached.filter((c) => c.currency_code === subscription.currency)
      : allAttached;

    if (attachedCurrencies.length === 0) {
      setCurrency(undefined as unknown as Currency);
      return;
    }

    const fallback = attachedCurrencies[0] as Currency;

    setCurrency((prev) => {
      if (!prev) return fallback;
      const stillAttached = attachedCurrencies.find(
        (c) => c.currency_code === prev.currency_code
      );
      return stillAttached ?? fallback;
    });
  }, [pricingTable, subscription]);

  const sessionId = pricingPageProps?.sessionId;
  const siteId = pricingPageProps?.siteId;
  const isSubscriberView = false;
  const checkoutCustomDomain = ''; // TODO: modify when introducing custom domain for checkout

  // Navigate the top-level frame (not just this iframe) so full-page
  // checkout replaces the embedder's page, not our nested document.
  const handleRedirect = useCallback(
    (url: string) => {
      const newTab = () => {
        window.open(url, '_blank', 'noopener,noreferrer');
      };

      if (isPreview) {
        newTab();
        return;
      }

      // Are we framed? Reading window.top throws in some cross-origin frames,
      // which itself means we are inside a (cross-origin) iframe.
      let inIframe: boolean;
      try {
        inIframe = window.self !== window.top;
      } catch {
        inIframe = true;
      }

      if (inIframe) {
        // Try to navigate the top-level browser context. Setting a cross-origin
        // top.location throws a SecurityError (or window.top is unreachable),
        // so fall back to a new tab instead of silently redirecting the iframe.
        try {
          const top = window.top;
          if (!top) throw new Error('window.top unavailable');
          top.location.href = url;
        } catch {
          newTab();
        }
        return;
      }

      // Top-level document: replace the current tab, fall back to a new tab.
      try {
        window.location.href = url;
      } catch {
        newTab();
      }
    },
    [isPreview]
  );

  const host = hostFromProps || '';
  const options: EventOptions = {
    host,
    pricingPageKey: pricingPageProps?.pricingPageKey,
    sessionId,
  };

  // Event handlers
  const redirectEventHandler = useCallback((event: CbSuccessEvent) => {
    if (event.redirectUrl) {
      if (window.top) {
        window.top.location = event.redirectUrl;
      }
    }
  }, []);

  const setVisitorEventHandler = useCallback((event: SetVisitorEvent) => {
    setVisitor(event);
  }, []);

  const setSubscriptionAttributesHandler = useCallback(
    (event: SetSubscriptionAttributesEvent) => {
      setSubscriptionAttributes(event);
    },
    []
  );

  const setQuantityEventHandler = useCallback((event: SetQuantityEvent) => {
    setQuantity(event.quantity);
  }, []);

  const setBillingAddressEventHandler = useCallback(
    (event: SetBillingAddressEvent) => {
      setBillingAddress(event);
    },
    []
  );

  const setShippingAddressEventHandler = useCallback(
    (event: SetShippingAddressEvent) => {
      setShippingAddress(event);
    },
    []
  );

  const setBusinessEntityEventHandler = useCallback(
    (event: SetBusinessEntityEvent) => {
      setBusinessEntityId(event.id);
    },
    []
  );

  const setCurrencyEventHandler = useCallback(
    (event: SetCurrencyEvent) => {
      if (!event.currency) return;
      if (subscription?.currency && event.currency !== subscription.currency) {
        return;
      }
      const attachedCurrencies =
        PricingTableHelper.getCurrenciesFromPricingTable(pricingTable);
      const foundCurrency = attachedCurrencies.find(
        (c) => c.currency_code === event.currency
      );
      if (foundCurrency) {
        setCurrency(foundCurrency);
      }
    },
    [pricingTable, subscription]
  );

  const setPriceVariantEventHandler = useCallback(
    (event: SetPriceVariantEvent) => {
      setPriceVariantId(event.id);
    },
    []
  );

  const setShowPriceEventHandler = useCallback((event: SetShowPriceEvent) => {
    setShowPrice({ configs: event.configs ?? [] });
  }, []);

  // Register message listeners
  useMessage<CbSuccessEvent>(
    CbEventKeys.SUCCESS,
    redirectEventHandler,
    options
  );
  useMessage<SetVisitorEvent>(
    EventKeys.SET_VISITOR,
    setVisitorEventHandler,
    options
  );
  useMessage<SetSubscriptionAttributesEvent>(
    EventKeys.SET_SUBSCRIPTION_ATTRIBUTES,
    setSubscriptionAttributesHandler,
    options
  );
  useMessage<SetQuantityEvent>(
    EventKeys.SET_QUANTITY,
    setQuantityEventHandler,
    options
  );
  useMessage<SetBillingAddressEvent>(
    EventKeys.SET_BILLING_ADDRESS,
    setBillingAddressEventHandler,
    options
  );
  useMessage<SetShippingAddressEvent>(
    EventKeys.SET_SHIPPING_ADDRESS,
    setShippingAddressEventHandler,
    options
  );
  useMessage<SetBusinessEntityEvent>(
    EventKeys.SET_BUSINESS_ENTITY,
    setBusinessEntityEventHandler,
    options
  );
  useMessage<SetCurrencyEvent>(
    EventKeys.SET_CURRENCY,
    setCurrencyEventHandler,
    options
  );
  useMessage<SetPriceVariantEvent>(
    EventKeys.SET_PRICE_VARIANT,
    setPriceVariantEventHandler,
    options
  );
  useMessage<SetShowPriceEvent>(
    EventKeys.SHOW_TOTAL_PRICE,
    setShowPriceEventHandler,
    options
  );

  useEffect(() => {
    const script = loadChargebeeScript(isProd, () => {
      initializeChargebee(siteId, businessEntityId, checkoutCustomDomain);
    });

    return () => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handlePrimaryCtaClick = async ({
    item,
    price,
    quantity,
    isSubscribedPrice,
    paywallConfig,
    addonsDataForCheckout,
    recommendedAddonsMap,
    mandatoryAddonsMap,
  }: {
    item: Item;
    price: Price;
    quantity: number;
    subscription?: Subscription;
    isSubscribedPrice?: boolean;
    paywallConfig?: PaywallConfig;
    addonsDataForCheckout?: any[];
    recommendedAddonsMap?: Record<string, number>;
    mandatoryAddonsMap?: Record<string, number>;
  }) => {
    if (isCheckoutLoading) return;
    setIsCheckoutLoading(true);
    try {
      if (pricingPageProps?.isDirty) {
        toast &&
          toast({
            description:
              'Please save as draft or publish your changes before initiating checkout session',
            variant: 'danger',
          });
        return Promise.reject(
          'Dirty pricing page, cannot initiate checkout session'
        );
      }

      const redirectUrlEnabledForSubscribedPrice =
        isSubscribedPrice &&
        paywallConfig?.action_button?.action ===
          SubscriberCTAAction.redirect_to_url &&
        paywallConfig?.action_button?.url;
      const redirectUrlEnabledForNonSubscribedPrice =
        item.customization.action_button?.type ===
          ActionButtonType.custom_url && item.customization.action_button?.url;

      if (
        sessionId === undefined &&
        isSubscriberView &&
        !redirectUrlEnabledForSubscribedPrice
      ) {
        toast({
          description:
            'Checkout for subscriber view is not allowed in preview mode',
          variant: 'warning',
        });
        return;
      }

      // send event
      const visitorId = getOrCreateVisitorId();
      const eventData = {
        app_id: pricingPageProps?.appId ?? '',
        source: EventSource.PRICING_PAGE,
        session: pricingPageProps?.sessionId ?? null,
        guid: `${visitorId}-${uuidv4()}`,
        id: uuidv4(),
        name: 'pricing_items_requested',
        event: 'pricing_items_requested',
        properties: {},
        pricing_page_properties: {
          visitor_id: visitorId,
          billing_system_id: price.billing_system_id,
          quantity,
          addons_with_quantity: {
            ...recommendedAddonsMap,
            ...mandatoryAddonsMap,
          },
          pricing_page_id: pricingPageProps?.pricingPageId,
          pricing_page_ev_id: pricingPageProps?.pricingPageEVId,
        },
      } as EventObject;

      // temporarily adding try-catch to block checkout, execute events failing in embedded iframe
      try {
        executeEvents({
          events: [eventData],
          synchronous: true,
        });
      } catch (error) {
        console.log('Error executing events:', error);
      }

      if (
        redirectUrlEnabledForNonSubscribedPrice ||
        redirectUrlEnabledForSubscribedPrice
      ) {
        const baseUrl = (
          isSubscribedPrice
            ? paywallConfig?.action_button?.url
            : item.customization.action_button?.url
        ) as string;
        const data = {
          item,
          price,
          visitor,
          subscriptionAttributes,
          quantity,
          billingAddress,
          shippingAddress,
          pricingPage: {
            pricingTable,
          },
          addons: addonsDataForCheckout,
          businessEntityId,
        };

        // Expose both snake_case (Growth) and camelCase (legacy Atomic Pricing)
        // field names so redirect URLs migrated from Atomic Pricing keep working.
        const url = mustache.render(
          baseUrl,
          withCamelCaseAliases(data),
          {},
          {
            escape: (value: string | number | boolean) =>
              encodeURIComponent(value),
          }
        );

        handleRedirect(url);
        return;
      }

      const cbInstance = initializeChargebee(
        siteId,
        businessEntityId,
        checkoutCustomDomain
      );
      if (!cbInstance) {
        toast({
          description:
            'Something went wrong, please refresh the page and try again.',
          variant: 'danger',
        });
        return;
      }
      const addons = addonsDataForCheckout?.map((addon) => {
        return {
          quantity: addon.quantity,
          price_id: addon.price?.billing_system_id,
        } as CreateCheckoutRequestAddon;
      });

      // Create checkout using direct fetch calls
      // Map camelCase (from SDK) -> snake_case (for API)
      try {
        let hostedPage: HostedPage;
        const appKey = pricingPageProps?.appId ?? '';
        const pricingPageId = pricingPageProps?.pricingPageId ?? '';

        // Common fields for both preview and live checkout
        const baseCheckoutData = {
          visitor_id: getOrCreateVisitorId(),
          price_id: price.billing_system_id,
          quantity,
          pricing_page_id: pricingPageId,
          pricing_page_version: 1,
          business_entity_id: businessEntityId,
          addons,
        };

        if (isPreview) {
          // Preview checkout - minimal data, no session/customer info
          const createPreviewCheckoutData: CreatePreviewCheckoutRequest = {
            ...baseCheckoutData,
            subscription_id: subscription?.id,
          };

          hostedPage = await createPreviewCheckout(
            appKey,
            pricingPageId,
            createPreviewCheckoutData
          );
        } else {
          // Live checkout - full data with session and customer info
          const createCheckoutData: CreateCheckoutRequest = {
            ...baseCheckoutData,
            visitor: visitor
              ? {
                  first_name: visitor.firstName,
                  last_name: visitor.lastName,
                  email: visitor.email,
                  phone: visitor.phone,
                  company: visitor.company,
                  custom_fields: visitor.customFields,
                }
              : undefined,
            subscription_attributes: subscriptionAttributes
              ? {
                  custom_fields: subscriptionAttributes.customFields,
                }
              : undefined,
            billing_address: billingAddress
              ? {
                  first_name: billingAddress.firstName,
                  last_name: billingAddress.lastName,
                  email: billingAddress.email,
                  company: billingAddress.company,
                  phone: billingAddress.phone,
                  line1: billingAddress.line1,
                  line2: billingAddress.line2,
                  line3: billingAddress.line3,
                  city: billingAddress.city,
                  state_code: billingAddress.stateCode,
                  state: billingAddress.state,
                  zip: billingAddress.zip,
                  country: billingAddress.country,
                  validation_status: billingAddress.validationStatus,
                }
              : undefined,
            shipping_address: shippingAddress
              ? {
                  first_name: shippingAddress.firstName,
                  last_name: shippingAddress.lastName,
                  email: shippingAddress.email,
                  company: shippingAddress.company,
                  phone: shippingAddress.phone,
                  line1: shippingAddress.line1,
                  line2: shippingAddress.line2,
                  line3: shippingAddress.line3,
                  city: shippingAddress.city,
                  state_code: shippingAddress.stateCode,
                  state: shippingAddress.state,
                  zip: shippingAddress.zip,
                  country: shippingAddress.country,
                  validation_status: shippingAddress.validationStatus,
                }
              : undefined,
            session_id: sessionId,
          };

          hostedPage = await createCheckout(
            appKey,
            pricingPageId,
            createCheckoutData
          );
        }

        if (hostedPage.layout === 'FULL_PAGE' || window.innerWidth <= 479) {
          handleRedirect(hostedPage.url);
          return;
        }

        // Open Chargebee checkout modal
        await cbInstance.openCheckout({
          hostedPage: async () => hostedPage,
          success: () => {
            toast({
              description: 'Checkout success!',
              variant: 'success',
            });
          },
        });
      } catch (error) {
        console.error('Checkout error:', error);

        const apiError = error as any;

        if (apiError?.status === 401) {
          toast({
            description: isPreview
              ? 'Using preview mode without being logged in!'
              : 'Please log in to proceed with the checkout!',
            variant: 'danger',
          });
          return;
        }

        // Extract error message from API response
        // The createCheckout function throws errors with error.data.message set
        let errorMessage = 'An unexpected error occurred. Please try again.';

        // Check if error has data property (from createCheckout's error handling)
        if (apiError?.data?.message) {
          errorMessage = apiError.data.message;
        }
        // Check if error has message property directly
        else if (apiError?.message) {
          errorMessage = apiError.message;
        }
        // Check if it's a standard Error object
        else if (error instanceof Error && error.message) {
          errorMessage = error.message;
        }

        toast({
          description: errorMessage,
          variant: 'danger',
        });
      }
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  return (
    <PricingTableContext.Provider
      value={{
        pricingTable,
        subscription,
        currency,
        frequency,
        priceVariantId,
        setCurrency,
        setFrequency,
        setPriceVariantId,
        quantity,
        setQuantity,
        priceQuantities,
        setPriceQuantity,
        isCheckoutLoading,
        handlePrimaryCtaClick,
        discounts,
        country: countryState,
        defaultCurrency,
        isPreview,
        isFullPreview,
        showCurrencyDropdown: showCurrencyDropdownProp,
        urlCurrencyCode: urlCurrency,
        legacySiteId,
        legacyPricingPageId,
        executeEvents,
        visitor,
        showPrice,
      }}
    >
      {!pricingTable ? (
        <PricingPageShimmerLoader isFullScreen={false} />
      ) : (
        children
      )}
    </PricingTableContext.Provider>
  );
};
